import * as React from "react";
import CclDrawer from "../../../components/common/cclDrawer/cclDrawer";
import { Box } from "@mui/material";
import {
    AdvancedSavedSearchItem,
    RegistrationSavedSearchItem,
    SessionSavedSearchItem,
} from "../../../services/types/enterpriseCoachingApiTypes";
import CclLoadingButton from "../../../components/common/cclButtons/cclLoadingButton";
import CclButton from "../../../components/common/cclButtons/cclButton";
import CclDrawerActionBar from "../../../components/common/cclDrawer/cclDrawerActionBar";
import SessionSearchCard from "../../registrations/advancedSearch/sessionsSearchCard";
import {
    AdvancedSearchRegistrationParameters,
    AdvancedSearchRequest,
    AdvancedSearchResults,
    AdvancedSearchSessionParameters,
} from "../../../services/types/enterpriseParticipantApiTypes";
import { useRunAdvancedSessionSearchMutation } from "../../../services/cclTokenedSessionApi";
import { useLazyGetAssessmentAdminGroupByEmailQuery } from "../../../services/cclTokenedAssessmentApi";
import CclAlertSnackbar from "../../../components/common/cclAlertSnackbar";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import { getUtcDateStringWithoutOffset } from "../../../utilities/dateHelpers";

interface SessionsAdvancedSearchControlProps {
    onSearchResults: (res: AdvancedSearchResults) => void;
    updateCurrentSearch: (search: any) => void;
    pastSearch: AdvancedSavedSearchItem;
    open: boolean;
    onClose: () => void;
}

export interface AttributeSearch {
    attributeType: AutocompleteItem | undefined;
    attributeValue: AutocompleteItem | undefined;
    attributeLevel: AutocompleteItem | undefined;
    id: string;
}

export interface AutocompleteItem {
    id: string;
    label: string;
}

const SessionsAdvancedSearchControl: React.FC<SessionsAdvancedSearchControlProps> = (props) => {
    const [runSearch, { isLoading }] = useRunAdvancedSessionSearchMutation();
    const [getAdminGroupByEmail, { isLoading: adminEmailLookupLoading }] =
        useLazyGetAssessmentAdminGroupByEmailQuery();
    const [openSnackbar, setOpenSnackbar] = React.useState<boolean>(false);
    const [message, setMessage] = React.useState<string>("");
    const [severity, setSeverity] = React.useState<"error" | "success" | undefined>(undefined);
    const { logEvent } = useLogAccessEvent();

    const executeSearch = (adminGroupKey?: number) => {
        if (!props.pastSearch) {
            console.log("no search criteria");
            return;
        }
        const registrationObj: AdvancedSearchRegistrationParameters = {
            status: props.pastSearch.registration.participantStatus
                ? +props.pastSearch.registration.participantStatus.id
                : undefined,
            firstName: props.pastSearch.registration.firstName
                ? props.pastSearch.registration.firstName.label
                : undefined,
            lastName: props.pastSearch.registration.lastName
                ? props.pastSearch.registration.lastName.label
                : undefined,
            email: props.pastSearch.registration.email
                ? props.pastSearch.registration.email.label
                : undefined,
        };
        const sessionObj: AdvancedSearchSessionParameters = {
            type: props.pastSearch.session.type ? +props.pastSearch.session.type.id : undefined,
            status: props.pastSearch.session.status
                ? +props.pastSearch.session.status.id
                : undefined,
            projectId: props.pastSearch.session.projectId
                ? props.pastSearch.session.projectId.label
                : undefined,
            sessionId: props.pastSearch.session.sessionId
                ? props.pastSearch.session.sessionId.label
                : undefined,
            title: props.pastSearch.session.title
                ? props.pastSearch.session.title.label
                : undefined,
            customer: props.pastSearch.session.customer
                ? props.pastSearch.session.customer.label
                : undefined,
            managingLocation: props.pastSearch.session.managingLocation
                ? +props.pastSearch.session.managingLocation.id
                : undefined,
            startDateFrom: props.pastSearch.session.startDateFrom
                ? getUtcDateStringWithoutOffset(
                      new Date(props.pastSearch.session.startDateFrom.label)
                  )
                : undefined,
            startDateTo: props.pastSearch.session.startDateTo
                ? getUtcDateStringWithoutOffset(
                      new Date(props.pastSearch.session.startDateTo.label)
                  )
                : undefined,
            aagKey:
                adminGroupKey ??
                (props.pastSearch.session.administratorGroup
                    ? +props.pastSearch.session.administratorGroup.id
                    : undefined),
        };

        const searchObj: AdvancedSearchRequest = {
            registrationParameters: registrationObj,
            sessionParameters: sessionObj,
        };

        runSearch(searchObj)
            .unwrap()
            .then((res) => {
                if (res && res.success) {
                    props.onSearchResults(res);
                    setMessage("Search completed");
                    setSeverity("success");
                    setOpenSnackbar(true);
                    logEvent("SessionSearchExecuted", null);
                } else if (res && !res.success) {
                    console.log("Search Error: ", res.message);
                    setMessage("Search failed: " + res.message);
                    setSeverity("error");
                    setOpenSnackbar(true);
                }
            });
    };

    const handleSearch = () => {
        const adminEmail = props.pastSearch.session.administratorEmail?.label?.trim();
        if (adminEmail) {
            getAdminGroupByEmail(adminEmail)
                .unwrap()
                .then((groups) => {
                    if (!groups || groups.length === 0) {
                        setMessage("No administrator group found for the specified email.");
                        setSeverity("error");
                        setOpenSnackbar(true);
                        return;
                    }
                    executeSearch(groups[0].groupId);
                })
                .catch(() => {
                    setMessage("No administrator group found for the specified email.");
                    setSeverity("error");
                    setOpenSnackbar(true);
                });
            return;
        }
        executeSearch();
    };

    const handleReset = () => {
        const sessionObj = {} as SessionSavedSearchItem;
        const registrationObj = {} as RegistrationSavedSearchItem;
        props.updateCurrentSearch({
            session: sessionObj,
            registration: registrationObj,
        });
        props.onClose();
    };

    return (
        <CclDrawer
            title={"Advanced Search"}
            open={props.open}
            onClose={props.onClose}
            width="standard"
        >
            <Box sx={{ overflow: "hidden", height: 1 }}>
                {openSnackbar && (
                    <CclAlertSnackbar
                        open={openSnackbar}
                        onClose={() => setOpenSnackbar(false)}
                        message={message}
                        severity={severity}
                    />
                )}
                <Box sx={{ overflow: "auto" }}>
                    <SessionSearchCard
                        updateCurrentSearch={props.updateCurrentSearch}
                        pastSearch={props.pastSearch}
                    />
                </Box>

                <CclDrawerActionBar>
                    <CclLoadingButton
                        loading={isLoading || adminEmailLookupLoading}
                        onClick={handleSearch}
                        disabled={false}
                        sx={{ m: 1 }}
                        mode={"primary"}
                    >
                        Search
                    </CclLoadingButton>
                    <CclButton sx={{ m: 1 }} onClick={props.onClose} mode={"secondary"}>
                        Close
                    </CclButton>
                    <CclButton
                        mode={"primary"}
                        sx={{ float: "right", m: 1, ml: "auto" }}
                        onClick={handleReset}
                    >
                        Reset
                    </CclButton>
                </CclDrawerActionBar>
            </Box>
        </CclDrawer>
    );
};

export default SessionsAdvancedSearchControl;
